import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { StatusBadge } from "@/components/badges";
import { FolderPlus, Loader2, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export function ProjectsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [projectForm, setProjectForm] = useState({ name: "", description: "" });
  const [renameProj, setRenameProj] = useState(null);
  const [deleteProj, setDeleteProj] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [projectOpen, setProjectOpen] = useState(false);

  const { data: projects = [] } = useQuery({
    queryKey: ["admin-projects"],
    queryFn: async () => (await api.get("/admin/projects")).data,
  });

  const invalidateAll = () => {
    qc.invalidateQueries();
  };

  const projectMut = useMutation({
    mutationFn: async () => (await api.post("/admin/projects", projectForm)).data,
    onSuccess: () => { toast.success(t("admin.toastProjectCreated")); setProjectOpen(false); setProjectForm({ name: "", description: "" }); invalidateAll(); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const renameMut = useMutation({
    mutationFn: async () => api.put(`/admin/projects/${renameProj.id}`, { name: renameProj.name, description: renameProj.description || "" }),
    onSuccess: () => {
      toast.success(t("admin.toastProjectUpdated"));
      setRenameProj(null);
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const deleteProjMut = useMutation({
    mutationFn: async () => api.delete(`/admin/projects/${deleteProj.id}`),
    onSuccess: () => {
      toast.success(t("admin.toastProjectDeleted"));
      setDeleteProj(null);
      setDeleteConfirm("");
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">{t("admin.tabProjects")}</CardTitle>
          <Button size="sm" onClick={() => setProjectOpen(true)} data-testid="admin-new-project-button"><FolderPlus className="mr-2 h-4 w-4" /> {t("admin.newProject")}</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader><TableRow><TableHead>{t("common.name")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead>{t("dashboard.parties")}</TableHead><TableHead className="w-48">{t("progress.thProgress")}</TableHead><TableHead className="w-14 text-right"></TableHead></TableRow></TableHeader>
            <TableBody>
              {projects.map((p) => (
                <TableRow key={p.id} data-testid={`admin-project-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="font-mono text-sm">{p.party_count}</TableCell>
                  <TableCell><div className="flex items-center gap-2"><ProgressBar value={p.overall_progress} className="h-1.5 flex-1" /><span className="font-mono text-xs">{p.overall_progress}%</span></div></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" title={t("admin.renameEditT")} onClick={() => setRenameProj({ id: p.id, name: p.name, description: p.description || "" })} data-testid={`rename-project-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" title={t("admin.deleteProjectT")} className="text-slate-400 hover:text-red-600" onClick={() => { setDeleteProj(p); setDeleteConfirm(""); }} data-testid={`delete-project-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!renameProj} onOpenChange={(o) => !o && setRenameProj(null)}>
        <DialogContent data-testid="rename-project-dialog">
          <DialogHeader><DialogTitle>{t("admin.renameTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("common.name")}</Label>
              <Input value={renameProj?.name || ""} onChange={(e) => setRenameProj({ ...renameProj, name: e.target.value })} data-testid="rename-project-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.description")}</Label>
              <Input value={renameProj?.description || ""} onChange={(e) => setRenameProj({ ...renameProj, description: e.target.value })} data-testid="rename-project-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => renameMut.mutate()} disabled={!renameProj?.name?.trim() || renameMut.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="rename-project-submit">
              {renameMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteProj} onOpenChange={(o) => { if (!o) { setDeleteProj(null); setDeleteConfirm(""); } }}>
        <DialogContent data-testid="delete-project-dialog">
          <DialogHeader><DialogTitle>{t("admin.deleteProjTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {t("admin.deleteProjBody", { name: deleteProj?.name })}
            </p>
            <div className="space-y-1.5">
              <Label>{t("admin.typeToConfirm")}</Label>
              <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder={deleteProj?.name} data-testid="delete-project-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteProj(null); setDeleteConfirm(""); }} data-testid="delete-project-cancel">{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={() => deleteProjMut.mutate()} disabled={deleteConfirm.trim() !== deleteProj?.name || deleteProjMut.isPending} data-testid="delete-project-submit">
              {deleteProjMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("admin.deletePermanently")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={projectOpen} onOpenChange={setProjectOpen}>
        <DialogContent data-testid="create-project-dialog">
          <DialogHeader><DialogTitle>{t("admin.newProjectTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("common.name")}</Label>
              <Input value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} data-testid="project-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.description")}</Label>
              <Input value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })} data-testid="project-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => projectMut.mutate()} disabled={!projectForm.name.trim() || projectMut.isPending} data-testid="create-project-submit-button">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
